import React from 'react';
import { MdStore, MdLocalShipping } from 'react-icons/md';
import { motion } from 'framer-motion';
import { OrderType } from '../../hooks/useCheckoutForm';

interface OrderTypeTabsProps {
  activeTab: OrderType;
  setActiveTab: (tab: OrderType) => void;
  isDeliveryEnabled?: boolean;
}

const ALL_TABS: {
  key: OrderType;
  label: string;
  icon: React.ReactNode;
}[] = [
  { key: 'pickup', label: 'Pickup', icon: <MdStore /> },
  { key: 'delivery', label: 'Delivery', icon: <MdLocalShipping /> },
];

export const OrderTypeTabs: React.FC<OrderTypeTabsProps> = ({
  activeTab,
  setActiveTab,
  isDeliveryEnabled = false,
}) => {
  // Filter tabs based on delivery enabled setting
  const TABS = isDeliveryEnabled 
    ? ALL_TABS 
    : ALL_TABS.filter(t => t.key === 'pickup');
  
  const activeIndex = TABS.findIndex(t => t.key === activeTab);
  
  // If only one tab (pickup only), don't show tabs at all
  if (TABS.length === 1) {
    return null;
  }

  return (
    <div className="w-full">
      <div
        role="tablist"
        aria-label="Order type"
        className="relative flex bg-gray-100 rounded-xl p-1 overflow-hidden"
      >
        {/* Sliding Indicator */}
        <motion.div
          className="absolute top-1 bottom-1 w-1/2 rounded-lg bg-red-500"
          initial={false}
          animate={{ x: `${activeIndex * 100}%` }}
          transition={{
            type: 'spring',
            stiffness: 180,
            damping: 28,
            mass: 0.8,
          }}
        />

        {TABS.map(tab => {
          const isActive = activeTab === tab.key;

          return (
            <button
              key={tab.key}
              role="tab"
              aria-selected={isActive}
              type="button"
              onClick={() => setActiveTab(tab.key)}
              className={`relative z-10 flex-1 flex items-center justify-center gap-2 py-3 text-sm sm:text-base font-semibold rounded-lg transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-400 ${
                isActive
                  ? 'text-white'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              <span className="text-lg">{tab.icon}</span>
              {tab.label}
            </button>
          );
        })}
      </div>
    </div>
  );
};
